import axios from 'axios';

export const SESSION_EXPIRED_EVENT =
  'leedana:session-expired';

const LOCAL_API_BASE_URL =
  'http://localhost:8000/api';

const getApiBaseUrl = () => {
  const configuredUrl =
    process.env.REACT_APP_API_BASE_URL?.trim();

  if (!configuredUrl) {
    if (process.env.NODE_ENV === 'production') {
      throw new Error(
        'REACT_APP_API_BASE_URL is required in production.'
      );
    }

    return LOCAL_API_BASE_URL;
  }

  let parsedUrl;

  try {
    parsedUrl = new URL(configuredUrl);
  } catch {
    throw new Error(
      'REACT_APP_API_BASE_URL must be a valid absolute URL.'
    );
  }

  if (
    parsedUrl.protocol !== 'http:' &&
    parsedUrl.protocol !== 'https:'
  ) {
    throw new Error(
      'REACT_APP_API_BASE_URL must use HTTP or HTTPS.'
    );
  }

  const localHostnames = new Set([
    'localhost',
    '127.0.0.1',
    '::1',
  ]);

  if (
    process.env.NODE_ENV === 'production' &&
    localHostnames.has(parsedUrl.hostname)
  ) {
    throw new Error(
      'REACT_APP_API_BASE_URL cannot use localhost in production.'
    );
  }

  return configuredUrl.replace(/\/+$/, '');
};

export const API_BASE_URL = getApiBaseUrl();

export const getApiError = (error) => {
  const responseData = error.response?.data;
  const status = error.response?.status || null;

  const isTimeout =
    error.code === 'ECONNABORTED' ||
    error.code === 'ETIMEDOUT';

  const isNetworkError =
    !error.response && !isTimeout;

  let message =
    responseData?.message ||
    responseData?.error;

  if (!message && isTimeout) {
    message =
      'The request took too long. Please try again.';
  }

  if (!message && isNetworkError) {
    message =
      'Unable to connect to the server. Check your connection and try again.';
  }

  if (!message) {
    message =
      error.message ||
      'Unable to complete the request.';
  }

  return {
    code:
      responseData?.code ||
      (isTimeout
        ? 'REQUEST_TIMEOUT'
        : isNetworkError
          ? 'NETWORK_ERROR'
          : 'REQUEST_FAILED'),

    message,
    status,
    isTimeout,
    isNetworkError,
  };
};

let expiredSessionToken = null;

export const clearSessionExpiryState = () => {
  expiredSessionToken = null;
};

const instance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000,
  headers: {
    Accept: 'application/json',
  },
});

instance.interceptors.request.use(
  (config) => {
    const accessToken =
      localStorage.getItem('_token');

    if (accessToken) {
      config.headers.Authorization =
        `Bearer ${accessToken}`;
    }

    return config;
  },

  (error) => Promise.reject(error)
);

instance.interceptors.response.use(
  (response) => response,

  (error) => {
    const apiError = getApiError(error);

    // Preserve the original Axios error for existing
    // services while also providing one normalized shape.
    error.apiError = apiError;

    const status = error.response?.status;
    const requestUrl =
      error.config?.url || '';

    const isAuthenticationRequest =
      requestUrl.startsWith('/auth/');

    const accessToken =
      localStorage.getItem('_token');

    if (
      status === 401 &&
      !isAuthenticationRequest &&
      accessToken &&
      expiredSessionToken !== accessToken
    ) {
      expiredSessionToken = accessToken;

      window.dispatchEvent(
        new CustomEvent(
          SESSION_EXPIRED_EVENT,
          {
            detail: apiError,
          }
        )
      );
    }

    return Promise.reject(error);
  }
);

export default instance;